/** Chase the people who still owe something today.
 *
 *  The list is built from the work, not from the staff list — so there is nobody here
 *  who has finished, and "Everyone" is therefore a safe button rather than a firm-wide
 *  broadcast. Anyone already messaged today is flagged rather than blocked: the Owner
 *  pressed this deliberately, and a second chase before a deadline is a real need.
 */
import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import { ErrorNote, Spinner } from '@/components/ui'

type Person = {
  user_id: string
  name: string
  responsible: number
  approver: number
  total: number
  refs: number[]
  last_sent: string
  unreachable: string
}
type Preview = { day: string; people: Person[] }
type Sent = {
  sent: { user_id: string; name: string; tasks: number; message_id: string | null }[]
  skipped: { user_id: string; name?: string; why: string }[]
}

/** "22 minutes ago" — near enough, and shorter than a timestamp to read at a glance. */
function ago(iso: string): string {
  if (!iso) return ''
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''
  const mins = Math.round((Date.now() - then) / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`
  const hrs = Math.round(mins / 60)
  return `${hrs} hour${hrs === 1 ? '' : 's'} ago`
}

function owes(p: Person): string {
  const bits = []
  if (p.responsible) bits.push(`${p.responsible} to finish`)
  if (p.approver) bits.push(`${p.approver} to approve`)
  return bits.join(' · ')
}

export function ChasePanel({ onClose }: { onClose: () => void }) {
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [note, setNote] = useState('')
  const [done, setDone] = useState<Sent | null>(null)
  const [err, setErr] = useState<unknown>(null)

  const preview = useQuery({
    queryKey: ['chase-preview'],
    queryFn: () => api.get<Preview>('/ops/chase'),
  })

  // Everyone reachable, pre-ticked. The common case is "chase the lot", and starting
  // from an empty list would make that the most tedious option on the screen.
  useEffect(() => {
    if (preview.data) {
      setPicked(new Set(preview.data.people
        .filter((p) => !p.unreachable).map((p) => p.user_id)))
    }
  }, [preview.data])

  const send = useMutation({
    mutationFn: (ids: string[]) => api.post<Sent>('/ops/chase', {
      user_ids: ids, note,
    }),
    onSuccess: (out) => { setErr(null); setDone(out) },
    onError: setErr,
  })

  const people = preview.data?.people ?? []
  const reachable = people.filter((p) => !p.unreachable)
  const toggle = (id: string) => setPicked((s) => {
    const next = new Set(s)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    return next
  })
  const allOn = reachable.length > 0 && reachable.every((p) => picked.has(p.user_id))

  return (
    <>
      <div className="fixed inset-0 bg-ink/10 z-10" onClick={onClose} />
      <aside className="fixed right-0 top-0 bottom-0 w-full sm:w-[480px] bg-surface
                        border-l border-rule shadow-2xl z-20 flex flex-col">
        <div className="px-5 py-3.5 border-b border-rule flex items-center">
          <h3 className="text-base font-semibold tracking-tight">
            Chase outstanding work
          </h3>
          <span className="flex-1" />
          <button onClick={onClose}
                  className="text-ink-3 hover:text-ink text-lg px-1">×</button>
        </div>

        <div className="flex-1 overflow-auto p-5 space-y-3.5">
          {err ? <ErrorNote error={err} /> : null}

          {done ? (
            <div className="space-y-3">
              <div className="rounded-md border border-rule bg-done/10 px-3 py-2.5">
                <b className="text-[13px]">
                  Sent to {done.sent.length} {done.sent.length === 1
                    ? 'person' : 'people'}.
                </b>
                <ul className="mt-1.5 space-y-0.5 text-[12.5px] text-ink-2">
                  {done.sent.map((s) => (
                    <li key={s.user_id}>
                      {s.name} — {s.tasks} task{s.tasks === 1 ? '' : 's'}
                    </li>))}
                </ul>
              </div>
              {done.skipped.length > 0 && (
                <div className="rounded-md border border-rule px-3 py-2.5">
                  <b className="text-[13px]">
                    {done.skipped.length} not sent
                  </b>
                  <ul className="mt-1.5 space-y-0.5 text-[12.5px] text-ink-3">
                    {done.skipped.map((s) => (
                      <li key={s.user_id}>{s.name ?? s.user_id} — {s.why}</li>))}
                  </ul>
                </div>
              )}
            </div>
          ) : preview.isLoading ? (
            <Spinner label="Working out who owes what" />
          ) : preview.error ? (
            <ErrorNote error={preview.error} />
          ) : people.length === 0 ? (
            <div className="rounded-md border border-rule px-3 py-6 text-center">
              <b className="text-[13px]">Nothing to chase.</b>
              <p className="mt-1 text-[12.5px] text-ink-3">
                Every task on today's list is finished or cancelled, and no approval is
                waiting. There is nobody this could truthfully be sent to.
              </p>
            </div>
          ) : (
            <>
              <p className="text-[12.5px] text-ink-3">
                Only people with unfinished work today appear here. Anyone who has
                finished cannot be chased.
              </p>

              <label className="flex items-center gap-2 text-[12.5px] font-medium
                                border-b border-rule pb-2">
                <input type="checkbox" checked={allOn}
                       onChange={() => setPicked(allOn ? new Set()
                         : new Set(reachable.map((p) => p.user_id)))} />
                Everyone with work outstanding ({reachable.length})
              </label>

              <ul className="space-y-1.5">
                {people.map((p) => (
                  <li key={p.user_id}>
                    <label className={`flex gap-2.5 items-start rounded-md border
                                       border-rule px-2.5 py-2 ${
                      p.unreachable ? 'opacity-60' : 'cursor-pointer'}`}>
                      <input type="checkbox" className="mt-0.5"
                             disabled={!!p.unreachable}
                             checked={picked.has(p.user_id)}
                             onChange={() => toggle(p.user_id)} />
                      <span className="min-w-0 flex-1">
                        <span className="flex items-baseline gap-2">
                          <b className="text-[13px]">{p.name}</b>
                          <span className="mono text-[11.5px] text-ink-3">
                            {owes(p)}
                          </span>
                        </span>
                        <span className="block mono text-[11px] text-ink-3 truncate">
                          {p.refs.map((r) => `#${r}`).join(' ')}
                        </span>
                        {p.unreachable ? (
                          <span className="block text-[11.5px] text-bad-ink">
                            Cannot be reached — {p.unreachable}
                          </span>
                        ) : p.last_sent ? (
                          <span className="block text-[11.5px] text-warn-ink">
                            Already messaged {ago(p.last_sent)}
                          </span>
                        ) : null}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>

              <div>
                <label className="label">Add a line of your own (optional)</label>
                <textarea rows={2} maxLength={300} value={note}
                          className="w-full rounded-md border border-rule px-2.5 py-2
                                     text-[13px]"
                          placeholder="The GST filing closes at 4pm today."
                          onChange={(e) => setNote(e.target.value)} />
              </div>
            </>
          )}
        </div>

        {!done && people.length > 0 && (
          <div className="px-5 py-3 border-t border-rule flex items-center gap-2">
            <span className="text-[12px] text-ink-3">
              {picked.size} selected
            </span>
            <span className="flex-1" />
            <button className="btn" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary"
                    disabled={send.isPending || picked.size === 0}
                    onClick={() => send.mutate([...picked])}>
              {send.isPending ? 'Sending…' : `Send to ${picked.size}`}
            </button>
          </div>
        )}
        {done && (
          <div className="px-5 py-3 border-t border-rule flex justify-end">
            <button className="btn btn-primary" onClick={onClose}>Done</button>
          </div>
        )}
      </aside>
    </>
  )
}
